import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  CreatePrescriptionRequestT,
  SendPrescriptionsRequestT,
  SendPrescriptionsResponseT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import type { RxRequest } from '@/entities/rx';
import {
  rxKeys,
  useCreatePrescriptionMutation,
  useSendPrescriptionsToPharmacyMutation,
} from '@/entities/rx';
import { extractApiErrorMessage } from '@/shared';
import { useSubmissionOrchestrator } from '@/shared/lib/submission';

export type RxSubmissionStatus =
  | 'idle'
  | 'creating'
  | 'sending'
  | 'success'
  | 'failed-create'
  | 'failed-send';

/**
 * Final disposition of a `send` step, mirroring the backend's per-row outcome
 * buckets. Surfacing this on the orchestrator state lets downstream UI render
 * the truth ("Prior auth sent, case #123") without having to look up the
 * Rx-wizard PA preview, which can disagree with the Rx-time gate.
 */
export type RxSendDisposition =
  | { kind: 'sent-to-pharmacy' }
  | { kind: 'prior-auth-initiated'; caseNumber: string | null }
  | { kind: 'excluded' }
  | { kind: 'divert-failed'; error: string };

export interface RxSubmissionState {
  status: RxSubmissionStatus;
  dosespotPrescriptionId: string | null;
  errorMessage: string | null;
  /**
   * Populated once a send step resolves. `null` until the first send attempt
   * completes (or while still `idle` / `creating`).
   */
  sendDisposition: RxSendDisposition | null;
}

const INITIAL_STATE: RxSubmissionState = {
  status: 'idle',
  dosespotPrescriptionId: null,
  errorMessage: null,
  sendDisposition: null,
};
const SUBMITTING_STATUSES: ReadonlySet<RxSubmissionStatus> = new Set(['creating', 'sending']);

export interface CreateRxSubmissionArgs {
  userId: string;
  rxRequest: RxRequest;
  sourceProposedActionExternalId?: string | null;
  diagnosis?: {
    diagnosisId?: number;
    diagnosisCode?: string;
    diagnosisDescription?: string;
  };
}

export type SourceProposedActionRequestFields = {
  sourceProposedActionExternalId?: string | null;
};

/**
 * Map a backend {@link SendPrescriptionsResponseT} plus the single prescription id
 * we submitted back to a {@link RxSendDisposition}. The Rx wizard sends a
 * one-element batch, so we look for the id in each bucket in turn.
 */
function deriveSendDisposition(
  response: SendPrescriptionsResponseT,
  dosespotPrescriptionId: string
): RxSendDisposition {
  const pa = response.priorAuthsInitiated.find(
    (entry) => entry.dosespotPrescriptionId === dosespotPrescriptionId
  );
  if (pa) {
    return { kind: 'prior-auth-initiated', caseNumber: pa.caseNumber ?? null };
  }
  if (response.excluded.includes(dosespotPrescriptionId)) {
    return { kind: 'excluded' };
  }
  const df = response.divertFailed.find(
    (entry) => entry.dosespotPrescriptionId === dosespotPrescriptionId
  );
  if (df) {
    return { kind: 'divert-failed', error: df.error };
  }
  // Default to "sent to pharmacy" — the backend guarantees each id lands in
  // exactly one bucket, so this is the residual case when none of the divert
  // buckets matched.
  return { kind: 'sent-to-pharmacy' };
}

export interface RxSubmissionOrchestrator {
  /** Current submission state */
  state: RxSubmissionState;

  /** Whether the draft is locked (prescription created, awaiting send or already sent) */
  isDraftLocked: boolean;

  /** Whether a submission operation is in progress */
  isSubmitting: boolean;

  /** Create a prescription in DoseSpot */
  create: (args: CreateRxSubmissionArgs) => Promise<{ dosespotPrescriptionId: string } | null>;

  /**
   * Send a created prescription to the pharmacy.
   *
   * Resolves to the per-row {@link RxSendDisposition} on success, or
   * ``null`` on failure. Returned directly (rather than forcing callers
   * to re-read ``state.sendDisposition``) so post-send callbacks don't
   * race against React's state update — the state version is one render
   * stale inside the very callback that awaited this promise.
   */
  send: (args: {
    userId: string;
    dosespotPrescriptionId: string;
    sourceProposedActionExternalId?: string | null;
    expedited?: boolean;
    paOnly?: boolean;
    skipPaGate?: boolean;
    patientEligibilityId?: number | null;
  }) => Promise<RxSendDisposition | null>;

  /** Create and send in one call - returns the dosespotPrescriptionId on success */
  createAndSend: (
    args: CreateRxSubmissionArgs
  ) => Promise<{ dosespotPrescriptionId: string } | null>;

  /**
   * Retry the send step. Same return contract as {@link send}.
   */
  retrySend: (
    userId: string,
    options?: {
      sourceProposedActionExternalId?: string | null;
      expedited?: boolean;
      paOnly?: boolean;
      skipPaGate?: boolean;
      patientEligibilityId?: number | null;
    }
  ) => Promise<RxSendDisposition | null>;

  /** Reset to initial idle state */
  reset: () => void;
}

export function useRxSubmissionOrchestrator(): RxSubmissionOrchestrator {
  const queryClient = useQueryClient();
  const createPrescriptionMutation = useCreatePrescriptionMutation();
  const sendToPharmacyMutation = useSendPrescriptionsToPharmacyMutation();
  const { state, isDraftLocked, isSubmitting, runPhase, patchState, reset } =
    useSubmissionOrchestrator<RxSubmissionState>({
      initialState: INITIAL_STATE,
      feature: 'rx-form',
      submittingStatuses: SUBMITTING_STATUSES,
      isDraftLocked: (current) => current.dosespotPrescriptionId !== null,
    });

  const invalidateQueries = useCallback(
    async (userId: string, dosespotPrescriptionId: string) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: rxKeys.patientPrescriptions(userId) }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.prescription(userId, dosespotPrescriptionId),
        }),
      ]);
    },
    [queryClient]
  );

  const create = useCallback(
    async (args: CreateRxSubmissionArgs): Promise<{ dosespotPrescriptionId: string } | null> => {
      return runPhase({
        pendingStatus: 'creating',
        execute: async () => {
          const request: CreatePrescriptionRequestT & SourceProposedActionRequestFields = {
            prescriptionData: args.rxRequest,
            sourceProposedActionExternalId: args.sourceProposedActionExternalId ?? null,
            ...(args.diagnosis
              ? {
                  diagnosis: {
                    // ZEKKA NOTE: This showed up after regenerating the SDK. Previously, the two objects were apparently compatible.
                    // I'm not sure if this is a bug in the SDK, in greenfield, or in Cortex.
                    // After checking Greenfield, these types look right.
                    diagnosis_id: args.diagnosis.diagnosisId,
                    diagnosis_code: args.diagnosis.diagnosisCode,
                    diagnosis_description: args.diagnosis.diagnosisDescription,
                  },
                }
              : {}),
          };

          const created = await createPrescriptionMutation.mutateAsync({
            userId: args.userId,
            data: request,
          });

          const id = created.PrescriptionId;
          if (id === null || id === undefined) {
            throw new Error('Create prescription response missing PrescriptionId.');
          }

          const dosespotPrescriptionId = String(id);
          await invalidateQueries(args.userId, dosespotPrescriptionId);
          return { dosespotPrescriptionId };
        },
        getSuccessPatch: (result) => ({ dosespotPrescriptionId: result.dosespotPrescriptionId }),
        getError: (error) => ({
          status: 'failed-create',
          message: extractApiErrorMessage(error, 'Could not create prescription.'),
          operation: 'create',
          logMessage: 'rx submission create failed',
        }),
      });
    },
    [createPrescriptionMutation, invalidateQueries, runPhase]
  );

  const send = useCallback(
    async (args: {
      userId: string;
      dosespotPrescriptionId: string;
      sourceProposedActionExternalId?: string | null;
      expedited?: boolean;
      paOnly?: boolean;
      skipPaGate?: boolean;
      patientEligibilityId?: number | null;
    }): Promise<RxSendDisposition | null> => {
      const result = await runPhase<{ disposition: RxSendDisposition }>({
        pendingStatus: 'sending',
        execute: async () => {
          const request: SendPrescriptionsRequestT & SourceProposedActionRequestFields = {
            dosespotPrescriptionIds: [args.dosespotPrescriptionId],
            sourceProposedActionExternalId: args.sourceProposedActionExternalId ?? null,
            expedited: args.expedited ?? null,
            paOnly: args.paOnly ?? null,
            skipPaGate: args.skipPaGate ?? null,
            patientEligibilityId: args.patientEligibilityId ?? null,
          };

          const response = await sendToPharmacyMutation.mutateAsync({
            userId: args.userId,
            data: request,
          });
          await invalidateQueries(args.userId, args.dosespotPrescriptionId);
          const disposition = deriveSendDisposition(response, args.dosespotPrescriptionId);
          if (disposition.kind === 'divert-failed') {
            throw new Error(disposition.error);
          }
          return { disposition };
        },
        getSuccessPatch: (result) => ({
          status: 'success',
          sendDisposition: result.disposition,
        }),
        getError: (error) => ({
          status: 'failed-send',
          message: extractApiErrorMessage(error, 'Could not send prescription.'),
          operation: 'send',
          logMessage: 'rx submission send failed',
        }),
      });

      return result?.disposition ?? null;
    },
    [invalidateQueries, runPhase, sendToPharmacyMutation]
  );

  const createAndSend = useCallback(
    async (args: CreateRxSubmissionArgs): Promise<{ dosespotPrescriptionId: string } | null> => {
      const created = await create(args);
      if (!created) {
        return null;
      }

      const disposition = await send({
        userId: args.userId,
        dosespotPrescriptionId: created.dosespotPrescriptionId,
        sourceProposedActionExternalId: args.sourceProposedActionExternalId,
      });

      return disposition !== null ? created : null;
    },
    [create, send]
  );

  const retrySend = useCallback(
    async (
      userId: string,
      options?: {
        sourceProposedActionExternalId?: string | null;
        expedited?: boolean;
        paOnly?: boolean;
        skipPaGate?: boolean;
        patientEligibilityId?: number | null;
      }
    ): Promise<RxSendDisposition | null> => {
      const id = state.dosespotPrescriptionId;
      if (!id) {
        patchState({
          status: 'failed-send',
          errorMessage: 'Missing created prescription.',
        });
        return null;
      }

      return send({ userId, dosespotPrescriptionId: id, ...options });
    },
    [patchState, send, state.dosespotPrescriptionId]
  );

  return {
    state,
    isDraftLocked,
    isSubmitting,
    create,
    send,
    createAndSend,
    retrySend,
    reset,
  };
}
